/**
 * LoaderProfilerTab - Компонент для отображения данных лоадеров из Kernel
 */

import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Statistic, Alert, Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import type { DebugProfilerInterface, DebugProfilerData } from './types'

interface LoaderProfilerTabProps {
    profiler: DebugProfilerInterface
}

/**
 * Компонент для отображения данных одного профайлера лоадера
 */
export const LoaderProfilerTab: React.FC<LoaderProfilerTabProps> = observer(({ profiler }) => {
    const [data, setData] = useState<DebugProfilerData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const loadData = async () => {
        try {
            setLoading(true)
            setError(null)

            const result = await profiler.getData(loadData)
            setData(result)
        } catch (err) {
            console.error(`Ошибка загрузки данных профайлера ${profiler.key}:`, err)
            setError(err instanceof Error ? err.message : 'Неизвестная ошибка')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (profiler.isEnabled?.() !== false) {
            loadData()
        }
    }, [profiler])

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Загрузка данных...</div>
            </div>
        )
    }

    if (error) {
        return (
            <Alert
                message="Ошибка загрузки данных"
                description={error}
                type="error"
                showIcon
                action={
                    <button
                        onClick={loadData}
                        style={{
                            border: 'none',
                            background: 'none',
                            color: '#1890ff',
                            cursor: 'pointer',
                        }}
                    >
                        Повторить
                    </button>
                }
            />
        )
    }

    if (!data) {
        return (
            <Alert
                message="Нет данных"
                description="Профайлер не вернул данных"
                type="info"
                showIcon
            />
        )
    }

    return (
        <div>
            {/* Статистические карточки */}
            {data.details.stats && data.details.stats.length > 0 && (
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    {data.details.stats.map((stat: any, index: number) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={index}>
                            <Card size="small">
                                <Statistic
                                    title={stat.title}
                                    value={stat.value}
                                    styles={{ content: { color: stat.color } }}
                                />
                                {stat.description && (
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                                        {stat.description}
                                    </div>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Дополнительное содержимое */}
            {data.details.custom && <div>{data.details.custom}</div>}

            {/* Таблицы (если есть) */}
            {data.details.tables &&
                data.details.tables.map((table: any, index: number) => (
                    <Card key={index} title={table.title} style={{ marginTop: 16 }}>
                        {/* Здесь можно добавить рендеринг таблиц если нужно */}
                        <div>Таблица будет добавлена в следующей версии</div>
                    </Card>
                ))}
        </div>
    )
})
